import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ExerciseCard } from '@fluent/ui';
import type { PhraseIntro as PhraseIntroExercise } from '@fluent/types';
import { useAudio } from '../audio/audioPlayer.js';
import { findPhrase } from './phraseMatch.js';
import './PhraseIntro.css';

export interface PhraseIntroProps {
  exercise: PhraseIntroExercise;
  locked: boolean;
}

/**
 * PhraseIntro — first-exposure teaching card. Big English phrase,
 * Georgian gloss underneath, audio that auto-plays once on mount.
 * No fail state. The bottom-dock SubmitBar's "გავიგე" CTA dispatches
 * ADVANCE_INTRO to step the lesson forward.
 *
 * The whole point of this exercise is teaching, not testing — so it
 * gives the learner the answer up front. Every new phrase in a lesson
 * passes through one of these before any harder exercise tests it.
 *
 * Phrases-course cards (PHRASES.md § 4) add, all optional: a register chip
 * under the phrase (informal / formal / slang — neutral shows nothing), the
 * word-for-word reading of a misleading idiom as a small trap line, and an
 * example sentence (phrase marked, own 🔊, Georgian under it). A card
 * without those fields renders exactly as before.
 */
export function PhraseIntro({ exercise, locked }: PhraseIntroProps) {
  const { t } = useTranslation();
  const audio = useAudio(exercise.audio_key);
  // Phrases course: the example sentence's clip (no-op for main-course cards).
  const exampleAudio = useAudio(exercise.example_audio_key);
  const autoPlayedRef = useRef<string | null>(null);

  useEffect(() => {
    if (autoPlayedRef.current === exercise.id) return;
    autoPlayedRef.current = exercise.id;
    audio.play();
  }, [exercise.id, audio]);

  // One clip at a time, and the engine does not tell a superseded clip's
  // owner — stop the other hook first so its button never stays "playing".
  const onTapAudio = () => {
    exampleAudio.stop();
    if (audio.error) {
      audio.retry();
      return;
    }
    if (audio.playing) audio.stop();
    else audio.play();
  };

  const onTapExampleAudio = () => {
    audio.stop();
    if (exampleAudio.error) {
      exampleAudio.retry();
      return;
    }
    if (exampleAudio.playing) exampleAudio.stop();
    else exampleAudio.play();
  };

  // "neutral" is the default register — only a marked one gets a chip.
  const register = exercise.register && exercise.register !== 'neutral' ? exercise.register : null;

  return (
    <ExerciseCard kicker={t('lesson.phrase_intro.kicker')}>
      <div className="fluent-intro" aria-disabled={locked || undefined}>
        <p className="fluent-intro__en" lang="en">
          {exercise.en}
        </p>

        {register && (
          <span className="fluent-intro__register" data-register={register} lang="ka">
            {t(`lesson.phrase_intro.register.${register}`)}
          </span>
        )}

        <div className="fluent-intro__audio-row">
          <AudioButton
            size="md"
            playing={audio.playing}
            onClick={onTapAudio}
            aria-label={t('lesson.phrase_intro.audio_aria')}
            data-testid="intro-audio-btn"
          />
          
          <span className="fluent-intro__audio-hint">
            {audio.error
              ? t('lesson.phrase_intro.audio_error')
              : t('lesson.phrase_intro.audio_hint')}
          </span>
        </div>

        <p className="fluent-intro__ka" lang="ka">
          {exercise.ka}
        </p>

        {exercise.literal_ka && (
          <p className="fluent-intro__literal" lang="ka" data-testid="intro-literal">
            <span className="fluent-intro__literal-label">{t('lesson.phrase_intro.literal_label')}</span>{' '}
            {exercise.literal_ka}
          </p>
        )}

        {exercise.example_en && (
          <div className="fluent-intro__example" data-testid="intro-example">
            <div className="fluent-intro__example-row">
              <p className="fluent-intro__example-en" lang="en">
                <EmphasisedPhrase sentence={exercise.example_en} phrase={exercise.en} />
              </p>
              {exercise.example_audio_key && (
                <AudioButton
                  size="sm"
                  className="fluent-intro__example-audio"
                  playing={exampleAudio.playing}
                  onClick={onTapExampleAudio}
                  label={t('lesson.phrase_intro.example_audio_aria')}
                  data-testid="intro-example-audio-btn"
                />
              )}
            </div>
            {exercise.example_ka && (
              <p className="fluent-intro__example-ka" lang="ka">
                {exercise.example_ka}
              </p>
            )}
          </div>
        )}

        {exercise.note_ka && (
          <p className="fluent-intro__note" lang="ka">
            {exercise.note_ka}
          </p>
        )}
      </div>
    </ExerciseCard>
  );
}

/** The example sentence with the card's phrase marked, when it occurs in it —
 *  as the shared matcher finds it (phraseMatch.ts: "pick someone up" marks
 *  "pick me up", "change your mind" marks "changed my mind"). */
function EmphasisedPhrase({ sentence, phrase }: { sentence: string; phrase: string }): ReactNode {
  const at = findPhrase(sentence, phrase);
  if (!at) return sentence;
  const [start, end] = at;
  return (
    <>
      {sentence.slice(0, start)}
      <mark className="fluent-intro__example-phrase">{sentence.slice(start, end)}</mark>
      {sentence.slice(end)}
    </>
  );
}
